import { useState } from 'react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { ImageIcon, Check, Loader2 } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import type { PackPreset } from '../types';

interface PresetCoverSelectorProps {
  presets: PackPreset[];
  onSelect: (coverUrl: string) => void | Promise<void>;
  disabled?: boolean;
}

export function PresetCoverSelector({
  presets,
  onSelect,
  disabled = false,
}: PresetCoverSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);

  const formatCover = (cover: string) =>
    cover.includes('firebase') ? cover : `${cover}?t=${Date.now()}`;

  const handleSelect = async (preset: PackPreset) => {
    setSelectedPresetId(preset.id);
    setIsConverting(true);
    try {
      await Promise.resolve(onSelect(preset.cover));
      setIsOpen(false);
    } finally {
      setIsConverting(false);
      setTimeout(() => setSelectedPresetId(null), 300);
    }
  };

  const hasPresets = presets.length > 0;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setIsOpen(true)}
        disabled={disabled || !hasPresets}
        size="sm"
        className={cn(
          'w-full bg-gradient-to-r from-orange-500/10 via-orange-500/8 to-orange-600/10',
          'border-orange-500/40 text-white',
          'hover:bg-gradient-to-r hover:from-orange-500/20 hover:via-orange-500/15 hover:to-orange-600/20',
          'hover:border-orange-500/60 hover:shadow-lg hover:shadow-orange-500/25',
          'transition-all duration-200 font-medium text-xs',
          (!hasPresets || disabled) && 'opacity-50 cursor-not-allowed'
        )}
      >
        <ImageIcon className="h-3.5 w-3.5 mr-2" />
        Usar foto de preset
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white text-xl font-semibold">
              Escolher foto de preset
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Selecione uma foto de um preset para usar como capa do pack
            </DialogDescription>
          </DialogHeader>

          {hasPresets ? (
            <div className="flex-1 overflow-y-auto pr-2 relative">
              {isConverting && (
                <div className="absolute inset-0 z-20 bg-zinc-900/80 flex flex-col items-center justify-center gap-3 rounded-lg">
                  <Loader2 className="h-10 w-10 animate-spin text-orange-400" />
                  <p className="text-white/80 text-sm">Carregando imagem...</p>
                </div>
              )}
              <div
                className={cn(
                  'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-1',
                  isConverting && 'pointer-events-none opacity-60'
                )}
              >
                {presets.map((preset) => {
                  const isSelected = selectedPresetId === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => handleSelect(preset)}
                      className={cn(
                        'group relative w-full rounded-2xl overflow-hidden shadow-lg transition-all duration-200',
                        'hover:scale-[1.02] hover:shadow-xl hover:shadow-orange-500/20',
                        'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-zinc-900',
                        isSelected && 'ring-2 ring-orange-500 scale-[1.02]'
                      )}
                    >
                      <div className="relative aspect-[4/5] w-full">
                        <img
                          src={formatCover(preset.cover)}
                          alt={preset.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-orange-500 rounded-full p-1.5 shadow-lg animate-in zoom-in duration-200">
                            <Check className="h-4 w-4 text-white" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <p className="text-white text-sm font-semibold drop-shadow-lg">
                            {preset.name}
                          </p>
                        </div>
                      </div>
                      <div className="bg-zinc-800/90 text-white px-3 py-2 text-sm font-medium border-t border-white/10">
                        {preset.name}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ImageIcon className="h-12 w-12 text-white/30 mb-4" />
              <p className="text-white/60 text-sm">
                Nenhum preset disponível para selecionar foto
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
